/**
 * Store: Index
 * -----------------------------------------------------------------------------
 * Root store for all theme level states.
 * - Tied to the index component.
 * - Only JSDOC custom parameters, ignore module state or object.
 *
 */
export default {
  state: () => ({
    hydrated: false,
    previousUrl: '/',
    screen: {
      breakpoint: 'xs',
      height: 0,
      mobile: true,
      width: 0,
    },
    settings: {
      conversionUnit: {
        index: 0,
        label: 'IN',
      },
      mobileBreakpoints: ['xs', 's', 'm'],
      start: 100,
      threshold: 5,
      throttle: 250,
    },
    scroll: {
      direction: 'none',
      header: 'open',
      paused: false,
      top: true,
      x: 0,
      y: 0,
    },
    stylesheets: [],
  }),

  mutations: {

    /**
     * Set size guide conversion unit preference
     * @param {object} unit
     */
    SET_CONVERSION_UNIT(state, unit) {
      state.settings.conversionUnit = unit
    },

    /**
     * Set rehydrated state.
     */
    SET_HYDRATED(state) {
      state.hydrated = true
    },

    /**
     * Set mobile breakpoints state.
     * @param {Array} mobileBreakpoints - Breakpoints considered as mobile.
     */
    SET_MOBILE_BREAKPOINTS(state, mobileBreakpoints) {
      state.settings.mobileBreakpoints = mobileBreakpoints
    },

    /**
     * Set paused scroll state.
     * @param {Object} paused - Paused scroll state.
     */
    SET_PAUSED_SCROLL(state, paused) {
      state.scroll.paused = paused
    },

    /**
     * Set previous URL state.
     * @param {String} pathname - Previous pathname.
     */
    SET_PREVIOUS_URL(state, pathname) {
      state.previousUrl = pathname
    },

    /**
     * Set screen state.
     * @param {Object} screen - Screen state object.
     */
    SET_SCREEN(state, screen) {
      state.screen = screen
    },

    /**
     * Set scroll state.
     * - Does not update paused state.
     * @param {Object} scroll - Scroll state object.
     */
    SET_SCROLL(state, scroll) {
      state.scroll = {
        ...scroll,
        paused: state.scroll.paused,
      }
    },

    /**
     * Set scroll start.
     * @param {String} start - New scroll start.
     */
    SET_SCROLL_START(state, start) {
      state.settings.start = start
    },

    /**
     * Set loaded stylesheets.
     * @param {String} stylesheet - Loaded stylesheet file name.
     */
    SET_STYLESHEET(state, stylesheet) {
      state.stylesheets.push(stylesheet)
    },
  },

  actions: {

    /**
     * Commit rehydrated state.
     * - Runs initial items.
     */
    async rehydrateSite({ dispatch, commit }) {
      commit('SET_HYDRATED')

      await Promise.all([dispatch('cart/setResponse', true)])
    },

    /**
     * Commit conversion unit preference.
     * @param {Object} unit - IN or CM
     */
    setConversionUnit({ commit }, unit) {
      commit('SET_CONVERSION_UNIT', unit)
    },

    /**
     * Commit mobile breakpoints state.
     * @param {Array} mobileBreakpoints - Breakpoints considered as mobile.
     */
    setMobileBreakpoints({ commit }, mobileBreakpoints) {
      commit('SET_MOBILE_BREAKPOINTS', mobileBreakpoints)
    },

    /**
     * Commit paused scroll state.
     * @param {Object} paused - Paused scroll state.
     */
    setPausedScroll({ commit }, paused) {
      commit('SET_PAUSED_SCROLL', paused)
    },

    /**
     * Commit previous URL state.
     * @param {String} pathname - Previous pathname.
     */
    setPreviousUrl({ commit }, pathname) {
      commit('SET_PREVIOUS_URL', pathname)
    },

    /**
     * Commit screen state.
     * @param {Object} screen - Screen state object.
     */
    setScreen({ commit }, screen) {
      commit('SET_SCREEN', screen)
    },

    /**
     * Commit scroll state.
     * @param {Object} scroll - Scroll state object.
     */
    setScroll({ commit }, scroll) {
      commit('SET_SCROLL', scroll)
    },

    /**
     * Commit scroll start setting state.
     * @param {String} start - New start.
     */
    setScrollStart({ commit }, start) {
      commit('SET_SCROLL_START', start)
    },

    /**
     * Commit loaded stylesheet state.
     * @param {String} stylesheet - Loaded stylesheet file name.
     */
    setStylesheet({ commit }, stylesheet) {
      commit('SET_STYLESHEET', stylesheet)
    },
  },
}
